<template>
  <div>
    <app-title-bar>
      <app-material-icon name="settings" />
      <h2>{{ $t('profile.views.Settings.settings') }}</h2>
    </app-title-bar>
    <div class="d-flex flex-column row-gap-6 layout-spacing">
      <AppCard class="pa-6 border" elevation="0">
        <v-row>
          <v-col cols="12">
            <h3 class="font-weight-bold">{{$t('profile.views.Settings.displayHeader')}}</h3>
          </v-col>
          <v-col cols="12">
            <AppSelect v-model="locale"
                      :items="languages"
                      :label="$t('profile.views.Settings.language')"
                      outlined
                      dense
                      :loading="updateLanguagePending"
                      @update:model-value="modifyLanguage"
            />
          </v-col>
        </v-row>

        <v-row class="mt-6 mb-2">
          <v-col cols="12">
            <v-divider />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <h3 class="font-weight-bold">{{$t('profile.views.Settings.notificationsPanelHeader')}}</h3>
          </v-col>
          <v-col cols="12">
            <AppSelect v-model="notificationsId"
                      :items="notificationsIdItems"
                      :label="$t('profile.views.Settings.defaultNotificationsPanelLabel')"
                      :loading="currentNotificationPreferencesPending || createNotificationPreferencesPending || deleteNotificationPreferencesPending || updateNotificationPreferencesPending"
            />
          </v-col>
          <v-col v-if="notificationsId !== 'default'" cols="12">
            <template v-if="noNotificationPreferences">
              <span>{{$t('profile.views.Settings.noNotificationsPreferences')}}</span>
              <v-btn class="ml-2" color="primary" variant="text" @click="createCustomPreferences">
                {{$t('profile.views.Settings.createCustomPreferences')}}
              </v-btn>
            </template>
            <template v-else-if="!currentNotificationPreferencesPending">
              <span>{{$t('profile.views.Settings.usesCustomPreferences')}}</span>
              <v-btn class="ml-2" color="primary" variant="text" @click="resetCustomPreferences">
                {{$t('profile.views.Settings.resetCustomPreferences')}}
              </v-btn>
            </template>
          </v-col>
        </v-row>

        <v-row class="mt-6">
          <v-col cols="12">
            <v-divider />
          </v-col>
        </v-row>

        <v-form v-if="!noNotificationPreferences"
                :disabled="currentNotificationPreferencesPending || updateNotificationPreferencesPending"
        >
          <v-row>
            <v-col cols="12">
              <h3 class="font-weight-bold">{{ $t('profile.views.Settings.documentsNotificationsHeader') }}</h3>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <NotificationSettings :label="$t('profile.views.Settings.newDocumentAccessNotifyLabel')"
                                    v-model="notificationPreferences['NewSharedDocument']"
                                    @settings-changed="updatePreferences"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <NotificationSettings :label="$t('profile.views.Settings.notifyBible')"
                                    v-model="notificationPreferences['BibleShared']"
                                    @settings-changed="updatePreferences"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-divider />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <h3 class="font-weight-bold">{{ $t('profile.views.Settings.electronicSignaturesHeader') }}</h3>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <NotificationSettings :label="$t('profile.views.Settings.notifyNewOriginalAvailable')"
                                    v-model="notificationPreferences['SignatureOriginalAvailable']"
                                    @settings-changed="updatePreferences"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <NotificationSettings :label="$t('profile.views.Settings.notifyOriginalShared')"
                                    v-model="notificationPreferences['SignatureOriginalShared']"
                                    @settings-changed="updatePreferences"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <NotificationSettings :label="$t('profile.views.Settings.notifyEnvelopeValidationChanged')"
                                    v-model="notificationPreferences['EnvelopeValidationChanged']"
                                    @settings-changed="updatePreferences"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <NotificationSettings :label="$t('profile.views.Settings.notifyUserCommentTodolist')"
                                    v-model="notificationPreferences['UserCommentNotifyUser']"
                                    @settings-changed="updatePreferences"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-divider />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <h3 class="font-weight-bold">{{ $t('profile.views.Settings.todolistHeader') }}</h3>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <NotificationSettings :label="$t('profile.views.Settings.notifyTodolistResponsible')"
                                    v-model="notificationPreferences['AgendaStepCreatedNotifyTaggedUser']"
                                    @settings-changed="updatePreferences"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <NotificationSettings :label="$t('profile.views.Settings.notifyTodolistGroupResponsible')"
                                    v-model="notificationPreferences['AgendaStepCreatedNotifyTaggedGroup']"
                                    @settings-changed="updatePreferences"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <NotificationSettings :label="$t('profile.views.Settings.notifyAgendaStepCreatedNotifyTaggedValidatorUser')"
                                    v-model="notificationPreferences['AgendaStepCreatedNotifyTaggedValidatorUser']"
                                    @settings-changed="updatePreferences"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <NotificationSettings :label="$t('profile.views.Settings.notifyAgendaStepCreatedNotifyTaggedValidatorGroup')"
                                    v-model="notificationPreferences['AgendaStepCreatedNotifyTaggedValidatorGroup']"
                                    @settings-changed="updatePreferences"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <NotificationSettings :label="$t('profile.views.Settings.notifyUserCommentNotifyTaggedUser')"
                                    v-model="notificationPreferences['UserCommentNotifyTaggedUser']"
                                    @settings-changed="updatePreferences"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <NotificationSettings :label="$t('profile.views.Settings.notifyUserCommentNotifyTaggedGroup')"
                                    v-model="notificationPreferences['UserCommentNotifyTaggedGroup']"
                                    @settings-changed="updatePreferences"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <NotificationSettings :label="$t('profile.views.Settings.notifyAgendaStepUpdateStatus')"
                                    v-model="notificationPreferences['AgendaStepUpdateStatus']"
                                    @settings-changed="updatePreferences"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-divider />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <h3 class="font-weight-bold">{{ $t('profile.views.Settings.questionAnswerHeader') }}</h3>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <NotificationSettings :label="$t('profile.views.Settings.notifyQAAnswerAdded')"
                                    v-model="notificationPreferences['QAAnswerAdded']"
                                    @settings-changed="updatePreferences"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <NotificationSettings :label="$t('profile.views.Settings.notifyQAQuestionShared')"
                                    v-model="notificationPreferences['QAQuestionShared']"
                                    @settings-changed="updatePreferences"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <NotificationSettings :label="$t('profile.views.Settings.notifyQAQuestionValidated')"
                                    v-model="notificationPreferences['QAQuestionValidated']"
                                    @settings-changed="updatePreferences"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-divider />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-switch
                  v-model="turnOffNotif"
                  class="mt-0"
                  @click="turnOffNotif ? prepareDisableNotification(true) : prepareShowTurnOffNotifDialog(true)"
              >
                <template #label>
                  <span>{{ $t("profile.views.Settings.disableAllNotification") }}</span>
                </template>
              </v-switch>
            </v-col>
          </v-row>
        </v-form>
      </AppCard>
    </div>
  </div>

  <AppDialog :is-open="showTurnOffNotifDialog"
             size="m"
             @ok="prepareDisableNotification(false)"
             @cancel="prepareShowTurnOffNotifDialog(false)"
  >
    <template #title>{{ $t('profile.views.Settings.disableNotificationDialog.title') }}</template>
    <template #body>{{ $t('profile.views.Settings.disableNotificationDialog.body') }}</template>
  </AppDialog>
</template>

<script>
import { nextTick } from 'vue'
import { mapActions, mapState } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import AppSelect from '@/common/AppSelect.vue'
import AppDialog from '@/common/dialogs/AppDialog.vue'
import { toast } from '@/common/utils/toast'
import NotificationSettings from '@/profile/NotificationSettings.vue'
import {
  CREATE_NOTIFICATION_PREFERENCES,
  DELETE_NOTIFICATION_PREFERENCES,
  GET_CURRENT_NOTIFICATION_PREFERENCES,
  UPDATE_LANGUAGE,
  UPDATE_NOTIFICATION_PREFERENCES,
  UPDATE_NOTIFICATION_MUTE_EMAILS,
} from '@/store/modules/user/action_types'

export default {
  name: 'Settings',
  components: { AppCard, NotificationSettings, AppDialog, AppSelect },
  data () {
    return {
      locale: '',
      // NOTE: The texts are hard coded on purpose, since they don't need to use the translator
      // We always display the translation for the corresponding language and it never changes
      languages: [
        {
          title: 'Français',
          value: 'fr',
        },
        {
          title: 'English',
          value: 'en',
        },
        {
          title: 'Deutsch',
          value: 'de',
        },
        {
          title: 'Español',
          value: 'es',
        },
      ],
      notificationsId: 'default',
      notificationPreferences: {},
      turnOffNotif: false,
      showTurnOffNotifDialog: false,
    }
  },
  computed: {
    ...mapState('user', [
      'profile',
      'updateLanguagePending',
      'updateNotificationPreferencesPending',
      'currentNotificationPreferencesPending',
      'currentNotificationPreferences',
      'createNotificationPreferencesPending',
      'deleteNotificationPreferencesPending',
    ]),
    ...mapState('rooms', ['rooms']),
    notificationsIdItems () {
      const defaultSettingsObj = {
        value: 'default',
        title: this.$t('profile.views.Settings.defaultNotificationsPanelLabel'),
      }
      if (this.rooms) {
        const roomItems = this.rooms.map((room) => ({
          value: room.id,
          title: room.name,
        }))
        return [
          defaultSettingsObj,
          ...roomItems,
        ]
      } else {
        return [
          defaultSettingsObj,
        ]
      }
    },
    noNotificationPreferences () {
      return Object.keys(this.currentNotificationPreferences).length === 0
    },
  },
  watch: {
    notificationsId: {
      async handler (newValue) {
        await this.GET_CURRENT_NOTIFICATION_PREFERENCES(newValue)
        this.notificationPreferences = { ...this.currentNotificationPreferences.preferences }
        this.turnOffNotif = this.currentNotificationPreferences.muteEmails === 1
      },
      immediate: true,
    },
  },
  created () {
    this.getRooms({ mini: true })
    this.locale = this.profile.locale
  },
  methods: {
    ...mapActions('user', [
      UPDATE_LANGUAGE,
      UPDATE_NOTIFICATION_PREFERENCES,
      UPDATE_NOTIFICATION_MUTE_EMAILS,
      CREATE_NOTIFICATION_PREFERENCES,
      DELETE_NOTIFICATION_PREFERENCES,
      GET_CURRENT_NOTIFICATION_PREFERENCES,
    ]),
    ...mapActions('rooms', ['getRooms']),
    async modifyLanguage () {
      await this.UPDATE_LANGUAGE(this.locale)
      this.$root.$i18n.locale = this.locale
      await nextTick()
      toast.success(this.$t('profile.views.Settings.langModified'))
    },
    async updatePreferences () {
      await this.UPDATE_NOTIFICATION_PREFERENCES({
        preferences: this.notificationPreferences,
        id: this.notificationsId,
      })
      toast.success(this.$t('profile.views.Settings.updateNotificationPreferencesSuccess'))
    },
    async createCustomPreferences () {
      await this.CREATE_NOTIFICATION_PREFERENCES(this.notificationsId)
      this.notificationPreferences = this.currentNotificationPreferences
    },
    async resetCustomPreferences () {
      this.DELETE_NOTIFICATION_PREFERENCES(this.notificationsId)
    },
    async prepareDisableNotification (allow) {
      if (allow) {
        // on autorise les notif
        this.turnOffNotif = false
      } else {
        // on bloque les notif
        this.turnOffNotif = true
        this.showTurnOffNotifDialog = false
      }
      await this.UPDATE_NOTIFICATION_MUTE_EMAILS({
        muteEmails: this.turnOffNotif ? 1 : 0, // Convert true/false to 0/1
      })
      toast.success(this.$t('profile.views.Settings.updateNotificationPreferencesSuccess'))
    },
    prepareShowTurnOffNotifDialog (display) {
      this.showTurnOffNotifDialog = display
      if (!display) {
        this.turnOffNotif = display
      }
    },
  },
}
</script>
